import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CatalogItem } from '../models/CatalogItem';
import { catalogService } from '../services/dataverse/catalogService';

export const useCatalog = () => {
  const [allItems, setAllItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [availability, setAvailability] = useState<'all' | 'available' | 'unavailable'>('all');

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAllItems(await catalogService.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load catalog.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadCatalog(); }, [loadCatalog]);

  // Client-side filtering — no API call on every keystroke
  const catalogItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allItems
      .filter((item) => category === 'all' || item.cr_crm_category === category)
      .filter((item) => availability === 'all' ? true : availability === 'available' ? item.cr_crm_available : !item.cr_crm_available)
      .filter((item) => !term || [item.cr_crm_skuname, item.cr_crm_productname, item.cr_crm_category].some((v) => v?.toLowerCase().includes(term)));
  }, [allItems, search, category, availability]);

  const refreshCatalog = useCallback(async () => {
    await catalogService.refresh();
    await loadCatalog();
  }, [loadCatalog]);

  return { catalogItems, loading, error, search, setSearch, category, setCategory, availability, setAvailability, reload: loadCatalog, refreshCatalog };
};
